import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, Renderer2, Signal } from '@angular/core';
import { ScrollingModule } from '@angular/cdk/scrolling';

import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { AppMaterialModule } from 'src/app/features/shared/material.module';
import { ButtonActionComponent } from 'src/app/features/shared/controls/container/button-action/button-action.component';
import { ListPositionComponent } from 'src/app/features/shared/lists/list-position/list-position.component';
import { ILevelGeneralListData } from 'src/app/infrastructure/models/level-general-list-data';
import { ILevelGeneralListDto } from 'src/app/infrastructure/models/level-general-list-dto';
import { LoadingPageService } from 'src/app/infrastructure/services/loading-services/loading-page.service';
import { LoadingTabService } from 'src/app/infrastructure/services/loading-services/loading-tab.service';
import { MatSelectionListChange } from '@angular/material/list';
import { PagerService } from 'src/app/features/shared/pager/pager.service';
import { SharedModule } from 'src/app/features/shared/shared.module';
import { TabDataService } from 'src/app/features/shared/central-page/services/tab-data.service';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { TablistFooterComponent } from 'src/app/features/shared/central-page/container/tablist/container/tablist-footer/tablist-footer.component';
import { _ListService } from 'src/app/infrastructure/services/list-services/_list.service';
import { _NavigationList_Base_ToolComponent } from 'src/app/infrastructure/components/tablist-components/_navigation-list-base-tool.component';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { ProgressBarService } from 'src/app/features/shared/progress-bar/progress-bar.service';
import { listButtonData } from '../../../models/_base-list-button';
import { structureLockedTooltip } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { tabMode, TTabModeDeleted } from 'src/app/infrastructure/models/tab-mode';

@Component({
    selector: 'app-navigation-list',
    templateUrl: './navigation-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [
        '../../../../central-page/central-page.scss',
        '../../../list.scss',
        './navigation-list.component.scss'
    ],
    standalone: true,
    imports: [
        ButtonActionComponent,
        ListPositionComponent,
        ScrollingModule,
        SharedModule,
        TablistFooterComponent,
        AppMaterialModule,
    ],
    animations: [
        showAnimation
    ]
})
export class NavigationList_ToolComponent
    extends _NavigationList_Base_ToolComponent
    implements AfterViewInit, OnInit, OnDestroy
{
    protected override readonly _activatedRouteService = injectRequired(ActivatedRouteService);
    protected override readonly _listService = injectRequired(_ListService);
    protected override readonly _loadingPageService = injectRequired(LoadingPageService);
    protected override readonly _loadingTabService = injectRequired(LoadingTabService);
    protected override readonly _pageService = injectRequired(_PageService);
    protected override readonly _pagerService = injectRequired(PagerService);
    protected override readonly _progressBarService = injectRequired(ProgressBarService);
    protected override readonly _tabDataService = injectRequired(TabDataService);
    protected override readonly _tabListService = injectRequired(TabListService);

    override input_btnAddData$$ = this._input_btnAddData$$;
    override input_btnDeleteData$$ = this._input_btnDeleteData$$;
    override input_buttonsHide$$ = this._input_buttonsHide$$;
    override input_claimPageList$$ = this._input_claimPageList$$;
    override input_itemLines$$ = this._input_itemLines$$;
    override input_itemSize$$ = this._input_itemSize$$;
    override input_noClick$$ = this._input_noClick$$;
    override input_pageNumber$$ = this._input_pageNumber$$;
    override input_pageSize$$ = this._input_pageSize$$;
    override input_template$$ = this._input_template$$;

    override out_onAdd = this._out_onAdd;
    override out_onClick = this._out_onClick;
    override out_onDeleteDeep = this._out_onDeleteDeep;
    override out_onRestore = this._out_onRestore;
    override out_onSave = this._out_onSave;

    override activeGuid$$ = this._activeGuid$$;
    override canClickThrough$$ = this._canClickThrough$$;
    override btnAddData$$ = this._btnAddData$$;
    override btnDeleteData$$ = this._btnDeleteData$$;
    override btnDeleteDeepData$$ = this._btnDeleteDeepData$$;
    override btnRestoreListData$$ = this._btnRestoreListData$$;
    override buttonsHide$$ = this._buttonsHide$$;
    override claimPageList$$ = this._claimPageList$$;
    override filterString$$ = this._filterString$$;
    override itemLines$$ = this._itemLines$$;
    override itemSize$$ = this._itemSize$$;
    override list$$ = this._list$$;
    override listActive$$ = this._listActive$$;
    override listAll$$ = this._listAll$$;
    override listDeleted$$ = this._listDeleted$$;
    override listDeletedAll$$ = this._listDeletedAll$$;
    override listFiltered$$ = this._listFiltered$$;
    override listInactive$$ = this._listInactive$$;
    override loadingPage$$ = this._loadingPage$$;
    override loadingTab$$ = this._loadingTab$$;
    override showSpinner$$ = this._showSpinner$$;
    override matList$$ = this._matList$$;
    override noClick$$ = this._noClick$$;
    override pageNumber$$ = this._pageNumber$$;
    override pageSize$$ = this._pageSize$$;
    override scrollElement$$ = this._scrollElement$$;
    override scrollPos$$ = this._scrollPos$$;
    override searchFields$$ = this._searchFields$$;
    override sortFields$$ = this._sortFields$$;
    override tabMode$$ = this._model_tabMode$$;
    override template$$ = this._input_template$$;
    override template_buttons$$ = this._view_template_buttons$$;
    override template_footer$$ = this._view_template_footer$$;

    filterMode$$ = this._pagerService.filterMode$$;

    deletedListShow$$ = computed<boolean>(() => this.tabMode$$() === tabMode.deletedList);
    inactiveListShow$$ = computed<boolean>(() => this.tabMode$$() === tabMode.inactiveList);
    mainListShow$$ = computed<boolean>(() => this.tabMode$$() === tabMode.mainList);
    hasInactiveList$$ = computed<boolean>(() => this.listInactive$$().length > 0);
    hasDeletedList$$ = computed<boolean>(() => this.listDeleted$$().length > 0);

    btnAddShow$$ = computed<boolean>(() => this.btnAddData$$()?.[listButtonData.hide] == false);
    btnAddDisabled$$ = computed<boolean>(() => this.btnAddData$$()?.[listButtonData.disabled] == true);
    btnAddTooltip$$ = computed<string>(() => this.btnAddDisabled$$() ? structureLockedTooltip : 'add to list');
    btnDeletedListShow$$ = computed<boolean>(() => this.btnDeleteData$$()?.[listButtonData.hide] == false);
    btnDeleteDeepShow$$ = computed<boolean>(() => this.btnDeleteDeepData$$()?.[listButtonData.hide] == false);
    btnDeleteDeepDisabled$$ = computed<boolean>(() => this.btnDeleteDeepData$$()?.[listButtonData.disabled] == true);
    btnRestoreDisabled$$ = computed<boolean>(() => this.btnRestoreListData$$()?.[listButtonData.disabled] == true);
    btnRestoreListShow$$ = computed<boolean>(() => this.btnRestoreListData$$()?.[listButtonData.hide] == false);

    constructor(
        protected _renderer?: Renderer2
    ) {
        super(_renderer);
    }

    override ngOnInit(): void {
        super._ngOnInit();
    }

    override emit_btnAdd_click(): void {
        super._btnAdd_emit();
    }

    override emit_btnRestore_click(): void {
        super._btnRestore_emit();
    }

    override emit_btnDeleteDeep_click(): void {
        super._btnDeleteDeep_emit();
    }

    override emit_change(event: MatSelectionListChange): void {
        const __item = event.options[0].value;
        const __deleted = __item.deleted;
        __item.deleted = !__deleted;
        this._btnRestore_setEnable(this.list$$().some(i => !i.deleted));
    }

    override emit_item_click(event: MouseEvent, item: ILevelGeneralListDto): void {
        super._emit_item_click(event, item);
    }

    override emit_onFilterChange(): void {
        super._emit_onFilterChange();
    }

    override emit_onScrollEnd(): void {
        this._onScrollEnd();
    }

    override emit_toggleDeleted(mode?: TTabModeDeleted): void {
        super._emit_toggleDeleted(mode);
    }

    override activeItem_set(item: ILevelGeneralListData): void {
        super._activeItem_set(item);
    }

    // compareWith(o1?: ILevelGeneralListData, o2?: ILevelGeneralListData): boolean {
    //     return super._compareWith(o1, o2);
    // }

    override isActivated(item: ILevelGeneralListData): boolean {
        return super._isActivated(item);
    }

    override toggleDeleted(mode?: TTabModeDeleted): void {
        super._toggleDeleted(mode);
    }

    override trackById(index: number, item: { guid: string; }): string {
        return super._trackById(item);
    }

    override ngAfterViewInit(): void {
        super._ngAfterViewInit();
    }

    override ngOnDestroy(): void {
        super._ngOnDestroy();
    }
}
